import { useState } from "react";
import { Badge, Button, Card, EmptyState, MUTED_TEXT, Spinner, stackStyle } from "@dayvise/react-ui";
import type { ConversationId } from "@dayvise/domain";
import { conversationOutputViews, useConversationOutputs } from "./conversationOutputs.ts";
import { useOutputRestorePreview, useRestoreOutput } from "./hooks.ts";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}

/**
 * Real file rollback for a conversation's retained outputs: pick a retained
 * revision, see exactly what would change against the workspace file now, and
 * restore it. Restoring never overwrites blindly — it writes a new version,
 * and refuses on a concurrent revision unless the current version is
 * confirmed. Transcript undo is a different, separate action.
 */
export function OutputRollbackPanel({ conversationId, branchId }: { conversationId: ConversationId | string; branchId: string | null }) {
  const outputs = useConversationOutputs(conversationId as ConversationId, branchId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const preview = useOutputRestorePreview(selectedId ? conversationId : null, selectedId, branchId);
  const restore = useRestoreOutput();
  const [confirming, setConfirming] = useState(false);
  const files = conversationOutputViews(outputs.data?.tree);
  const selected = files.find((file) => file.snapshotFileId === selectedId) ?? null;
  const previewData = preview.data?.preview ?? null;

  return (
    <Card
      title="Roll back written files"
      subtitle="Restore an exact retained revision from this conversation's runs. The restore writes a new version of the file; it does not overwrite the current one silently."
    >
      <div style={stackStyle("var(--dayvise-space-sm)")}>
        {outputs.isPending ? <Spinner label="Loading outputs" /> : null}
        {!outputs.isPending && files.length === 0 ? (
          <EmptyState title="Nothing to roll back" description="Retained file versions from this conversation's runs appear here once runs have written files." />
        ) : null}
        {files.map((file) => (
          <label key={file.snapshotFileId} style={{ display: "flex", gap: "var(--dayvise-space-xs)", alignItems: "center" }}>
            <input
              type="radio"
              name="rollback-target"
              checked={selectedId === file.snapshotFileId}
              onChange={() => {
                setSelectedId(file.snapshotFileId);
                setConfirming(false);
              }}
            />
            <span>
              {file.path} <span style={MUTED_TEXT}>retained v{file.version} · {formatBytes(file.size)}</span>
            </span>
          </label>
        ))}
        {selected ? (
          <div style={stackStyle("var(--dayvise-space-xs)")}>
            {preview.isFetching ? <Spinner label="Comparing revisions" /> : null}
            {previewData ? (
              <>
                <div style={{ display: "flex", gap: "var(--dayvise-space-xs)", alignItems: "center", flexWrap: "wrap" }}>
                  <Badge tone={previewData.action === "unchanged" ? "neutral" : previewData.action === "create" ? "success" : "warning"}>
                    {previewData.action === "create" ? "creates the file" : previewData.action === "overwrite" ? "file changed since this revision" : "identical to current"}
                  </Badge>
                </div>
                <p style={{ ...MUTED_TEXT, margin: 0 }}>
                  Current: {previewData.current ? `v${previewData.current.version} · ${formatBytes(previewData.current.size)}` : "absent"} · Target: v{previewData.target.revision} · {formatBytes(previewData.target.size)}
                  {previewData.target.runId ? <> · from run {previewData.target.runId}</> : null}
                </p>
                {previewData.action !== "unchanged" ? (
                  confirming ? (
                    <div style={{ display: "flex", gap: "var(--dayvise-space-xs)" }}>
                      <Button
                        variant="danger"
                        disabled={restore.isPending}
                        onClick={() => {
                          restore.mutate(
                            {
                              conversationId,
                              snapshotFileId: selected.snapshotFileId,
                              expectedVersion: previewData.current?.version,
                              branchId: branchId ?? undefined,
                            },
                            { onSuccess: () => setConfirming(false) },
                          );
                        }}
                      >
                        {restore.isPending ? "Restoring…" : "Restore this revision"}
                      </Button>
                      <Button onClick={() => setConfirming(false)}>Cancel</Button>
                    </div>
                  ) : (
                    <div>
                      <Button onClick={() => setConfirming(true)} disabled={restore.isPending}>
                        Restore…
                      </Button>
                    </div>
                  )
                ) : null}
              </>
            ) : null}
            {restore.data ? (
              <p style={{ ...MUTED_TEXT, margin: 0 }}>
                Restored {restore.data.path} as v{restore.data.version} (from retained v{restore.data.from.revision}).
              </p>
            ) : null}
            {restore.error ? (
              <p role="alert" style={{ color: "var(--dayvise-color-danger)", margin: 0 }}>
                {restore.error instanceof Error ? restore.error.message : "Restore failed"}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
